import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, Copy, Sparkles, Loader2, X, Upload, Building2, Link2, ShoppingCart, Anchor, Search, LayoutGrid, List as ListIcon, Star, Hash, Info, Globe, RefreshCw, AlertTriangle, DollarSign, Sheet as SheetIcon } from 'lucide-react';
import { toursChartersApi, uploadTourImage } from './toursChartersApi';
import { toast } from '../../../hooks/use-toast';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../../components/ui/accordion';

const inputCls = 'w-full rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-teal-500/50';
const emptyActivity = {
  title: '', alias: '', seller_id: '', category_ids: [], tags: [], images: [],
  description: '', short_description: '', price_display: '', price_from: '', duration: '', location: '',
  status: 'published', priority: 0, featured: false,
  booking_type: 'external_link', booking_provider: 'generic', booking_url: '', fareharbor_shortname: '', fareharbor_item_pk: '',
  seo_title: '', seo_description: '', seo_robots: 'index_follow',
};
const ROBOTS_OPTIONS = [
  { value: 'index_follow', label: 'Index, Follow (recommended)' },
  { value: 'index_nofollow', label: 'Index, No Follow' },
  { value: 'noindex_follow', label: 'No Index, Follow' },
  { value: 'noindex_nofollow', label: 'No Index, No Follow' },
];
const accordionItemCls = 'rounded-xl border border-white/10 bg-[#061a1f]/60 px-4 border-b-0 mb-3 last:mb-0';

/** Main categories first, each immediately followed by its own sub-categories, so the
 *  picker reads like the category tree. Activities can be in as many as you like. */
const orderCategories = (cats) => {
  const out = [];
  cats.filter((c) => !c.parent_id).forEach((top) => {
    out.push(top);
    cats.filter((c) => c.parent_id === top.id).forEach((child) => out.push(child));
  });
  cats.forEach((c) => { if (!out.includes(c)) out.push(c); });
  return out;
};

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSellerModal, setNewSellerModal] = useState(null); // {name, fareharbor_shortname}
  const [viewMode, setViewMode] = useState('block'); // block | list
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSellerId, setFilterSellerId] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  // The SEO preview domain and the FareHarbor commission reference are never hardcoded:
  // they come from the settings the owner manages (Site URL + FareHarbor attribution).
  const [siteInfo, setSiteInfo] = useState({ siteUrl: '', asn: 'fhdn', asnRef: '' });
  const [linkCheck, setLinkCheck] = useState(null);      // report from the booking link checker
  const [linkChecking, setLinkChecking] = useState(false);
  const [scraperHealth, setScraperHealth] = useState([]); // per-domain price-scraper health/alerts
  const [priceScraping, setPriceScraping] = useState(false);
  const [priceResult, setPriceResult] = useState(null);    // {success, price, candidates, blocked, error}
  const [fhdnPreview, setFhdnPreview] = useState(null);    // dry-run result, awaiting confirm
  const [fhdnResult, setFhdnResult] = useState(null);      // applied-sync result
  const [fhdnBusy, setFhdnBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      fetch(`${base}/api/settings/site`).then((r) => r.json()).catch(() => ({})),
      fetch(`${base}/api/settings/feature-flags`).then((r) => r.json()).catch(() => ({})),
    ]).then(([site, flags]) => setSiteInfo({
      siteUrl: (site?.site_url || '').replace(/^https?:\/\//i, '').replace(/\/+$/, ''),
      asn: flags?.fareharbor_asn || 'fhdn',
      asnRef: flags?.fareharbor_asn_ref || '',
    }));
  }, []);

  /** Exactly the URL the public site will open, including the commission attribution. */
  /** Opens every booking link for real and reports the ones the operator no longer serves. */
  const runLinkCheck = async () => {
    setLinkChecking(true);
    setLinkCheck(null);
    try {
      const res = await toursChartersApi.checkBookingLinks();
      setLinkCheck(res.data);
      toast({
        title: res.data.broken_count ? `${res.data.broken_count} booking link(s) are broken` : 'All booking links work',
        description: `${res.data.working}/${res.data.checked} opened successfully`,
        variant: res.data.broken_count ? 'destructive' : undefined,
      });
    } catch (e) {
      toast({ title: 'Link check failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLinkChecking(false);
    }
  };

  const resolvedBookingUrl = (m) => {
    if (m.booking_type !== 'external_link') return '';
    const seller = sellers.find((x) => x.id === m.seller_id);
    const ref = seller?.fareharbor_asn_ref || siteInfo.asnRef;
    const track = (url) => {
      if (!ref || !url.includes('fareharbor.com') || url.includes('asn-ref=')) return url;
      return `${url}${url.includes('?') ? '&' : '?'}asn=${siteInfo.asn || 'fhdn'}&asn-ref=${ref}&ref=${ref}`;
    };
    // Same order the public site uses: the activity's own link wins, then shortname + item id.
    const stored = (m.booking_url || '').trim()
      .replace(/\/calendar\/\d{4}\/\d{1,2}\//, '/')
      .replace(/([?&])back=[^&]*/g, '$1')
      .replace(/[?&]$/, '');
    if (m.booking_provider !== 'fareharbor') return stored;
    if (stored.includes('fareharbor.com/embeds/')) return track(stored);
    const shortname = m.fareharbor_shortname || seller?.fareharbor_shortname || '';
    if (!shortname) return '';
    const item = m.fareharbor_item_pk ? `items/${m.fareharbor_item_pk}/` : '';
    return track(`https://fareharbor.com/embeds/book/${shortname}/${item}?full-items=yes`);
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      toursChartersApi.listActivities(),
      toursChartersApi.listSellers(),
      toursChartersApi.listCategories(),
    ]).then(([a, s, c]) => {
      setActivities(a.data || []);
      setSellers(s.data || []);
      setCategories(c.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    toursChartersApi.scraperHealth().then((res) => setScraperHealth(res.data || [])).catch(() => {});
  }, []);

  /** Reads the price straight off the activity's own booking page (no FareHarbor API access -
   *  see backend/price_scraper.py). Always logged server-side so a run of blocks/failures for a
   *  domain raises the alert banner above, not just a quiet failure on this one click. */
  const scrapeActivityPrice = async () => {
    if (!modal?.id) { toast({ title: 'Save the activity first', description: 'The booking link is only checked on a saved activity.' }); return; }
    setPriceScraping(true);
    setPriceResult(null);
    try {
      const res = await toursChartersApi.scrapePrice(modal.id);
      setPriceResult(res.data);
      toursChartersApi.scraperHealth().then((r) => setScraperHealth(r.data || [])).catch(() => {});
      if (!res.data.success) {
        toast({ title: res.data.blocked ? 'Booking site is blocking automated requests' : 'No price found', description: res.data.error, variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Price check failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setPriceScraping(false);
    }
  };

  const applyScrapedPrice = (priceText) => {
    const numeric = (priceText || '').replace(/[^0-9.]/g, '');
    setModal((m) => ({ ...m, price_from: numeric, price_display: m.price_display || `Starting at ${priceText}` }));
    setPriceResult(null);
    toast({ title: 'Price filled in - review before saving' });
  };

  /** Preview (dry_run=true, nothing written) then a second click actually applies it. Safe to
   *  re-run any time the sheet changes - see backend/fhdn_sheet_sync.py for exactly what it
   *  does and does not touch. */
  const previewFhdnSync = async () => {
    setFhdnBusy(true);
    setFhdnResult(null);
    try {
      const res = await toursChartersApi.fhdnSync(true);
      setFhdnPreview(res.data);
    } catch (e) {
      toast({ title: 'Could not read the FHDN sheet', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setFhdnBusy(false);
    }
  };

  const applyFhdnSync = async () => {
    setFhdnBusy(true);
    try {
      const res = await toursChartersApi.fhdnSync(false);
      setFhdnResult(res.data);
      setFhdnPreview(null);
      toast({ title: 'FHDN sheet synced', description: `${res.data.created_activities.length} created, ${res.data.updated_activities.length} updated` });
      load();
    } catch (e) {
      toast({ title: 'Sync failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setFhdnBusy(false);
    }
  };

  const uploadImg = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadTourImage(file);
      setModal((m) => ({ ...m, images: [...(m.images || []), url] }));
    } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadImg(file);
  };

  const removeImg = (idx) => setModal((m) => ({ ...m, images: m.images.filter((_, i) => i !== idx) }));

  const toggleCategory = (id) => setModal((m) => ({
    ...m,
    category_ids: m.category_ids.includes(id) ? m.category_ids.filter((c) => c !== id) : [...m.category_ids, id],
  }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!modal.tags.includes(t)) setModal((m) => ({ ...m, tags: [...m.tags, t] }));
    setTagInput('');
  };
  const removeTag = (t) => setModal((m) => ({ ...m, tags: m.tags.filter((x) => x !== t) }));

  const createQuickSeller = async () => {
    if (!newSellerModal?.name?.trim()) { toast({ title: 'Company name required', variant: 'destructive' }); return; }
    try {
      const res = await toursChartersApi.createSeller({ name: newSellerModal.name.trim(), fareharbor_shortname: (newSellerModal.fareharbor_shortname || '').trim() });
      setSellers((prev) => [...prev, res.data]);
      setModal((m) => ({ ...m, seller_id: res.data.id }));
      setNewSellerModal(null);
      toast({ title: 'Charter company added' });
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const save = async () => {
    if (!modal.title?.trim()) { toast({ title: 'Title required', variant: 'destructive' }); return; }
    if (!modal.seller_id) { toast({ title: 'Select a charter company', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (modal.id) await toursChartersApi.updateActivity(modal.id, modal);
      else await toursChartersApi.createActivity(modal);
      toast({ title: modal.id ? 'Activity updated' : 'Activity created' });
      setModal(null); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete activity "${title}"?`)) return;
    await toursChartersApi.deleteActivity(id); toast({ title: 'Deleted' }); load();
  };

  /** Opens the New-Activity form pre-filled with everything from an existing activity, so
   *  admin only has to change the 1-2 fields that are actually different before saving.
   *  Never touches the original: no id -> Save Activity creates a brand new record. */
  const duplicateActivity = (a) => {
    const { id, created_at, updated_at, seller_name, ...rest } = a;
    setModal({ ...emptyActivity, ...rest, title: `${a.title} (Copy)`, alias: '' });
    setTagInput('');
    toast({ title: 'Copied - change what you need, then Save' });
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || '';

  const filteredActivities = activities.filter((a) => {
    if (searchQuery.trim() && !a.title.toLowerCase().includes(searchQuery.trim().toLowerCase())) return false;
    if (filterSellerId && a.seller_id !== filterSellerId) return false;
    if (filterCategoryId && !a.category_ids?.includes(filterCategoryId)) return false;
    return true;
  });
  const hasActiveFilters = searchQuery.trim() || filterSellerId || filterCategoryId;
  const clearFilters = () => { setSearchQuery(''); setFilterSellerId(''); setFilterCategoryId(''); };

  return (
    <div className="-m-4 min-h-screen bg-[#061a1f] p-5 text-white lg:-m-6 lg:p-8" data-testid="activities-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-black"><Sparkles className="h-6 w-6 text-teal-400" /> Activities</h1>
          <p className="text-sm text-white/40">{filteredActivities.length} of {activities.length} activities across {sellers.length} charter companies</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={previewFhdnSync}
            disabled={fhdnBusy}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white disabled:opacity-50"
            title="Preview what would change from the Client FHDN Integration Google Sheet"
            data-testid="fhdn-sync-btn"
          >
            {fhdnBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SheetIcon className="h-4 w-4" />}
            {fhdnBusy ? 'Reading sheet...' : 'Resync FHDN Sheet'}
          </button>
          <button
            onClick={runLinkCheck}
            disabled={linkChecking}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white disabled:opacity-50"
            title="Opens every booking link and reports the ones the operator no longer serves"
            data-testid="check-booking-links-btn"
          >
            {linkChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {linkChecking ? 'Checking links...' : 'Check Booking Links'}
          </button>
          <button onClick={() => { setModal({ ...emptyActivity }); setTagInput(''); }} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2.5 text-sm font-semibold" data-testid="add-activity-btn">
            <Plus className="h-4 w-4" /> Add Activity
          </button>
        </div>
      </div>

      {scraperHealth.filter((h) => h.alerting).map((h) => (
        <div key={h.domain} className="mb-5 flex items-start gap-3 rounded-xl border border-red-400/30 bg-red-400/5 p-4" data-testid={`scraper-alert-${h.domain}`}>
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-300" />
          <div>
            <p className="font-semibold text-red-200">
              The price scraper for {h.domain} has failed {h.consecutive_failures} times in a row{h.last_block_reason ? ` (${h.last_block_reason})` : ''}.
            </p>
            <p className="text-xs text-white/50">It may be blocking automated requests, or its page layout changed. Enter prices manually for this site until this is resolved.</p>
          </div>
        </div>
      ))}

      {fhdnPreview && (
        <div className="mb-5 rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-4" data-testid="fhdn-preview-report">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">FHDN sheet preview - nothing has been written yet</p>
              <p className="text-xs text-white/50">
                {fhdnPreview.complete_rows} of {fhdnPreview.sheet_rows} sheet rows have a FareHarbor Item Number and are safe to sync
                ({fhdnPreview.incomplete_rows} still need one filled in on the sheet first).
              </p>
            </div>
            <button onClick={() => setFhdnPreview(null)} className="rounded-lg p-1.5 text-white/40 hover:bg-white/10" data-testid="fhdn-preview-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div className="rounded-lg bg-black/20 p-2"><span className="block text-white/40">New companies</span><span className="font-bold">{fhdnPreview.created_sellers.length}</span></div>
            <div className="rounded-lg bg-black/20 p-2"><span className="block text-white/40">New categories</span><span className="font-bold">{fhdnPreview.created_categories.length}</span></div>
            <div className="rounded-lg bg-black/20 p-2"><span className="block text-white/40">New activities</span><span className="font-bold">{fhdnPreview.created_activities.length}</span></div>
            <div className="rounded-lg bg-black/20 p-2"><span className="block text-white/40">Activities to update</span><span className="font-bold">{fhdnPreview.updated_activities.length}</span></div>
          </div>
          {fhdnPreview.incomplete_examples?.length > 0 && (
            <details className="mt-3 text-xs text-white/50">
              <summary className="cursor-pointer text-white/70">Sample rows still missing an Item Number (not synced)</summary>
              <ul className="mt-2 space-y-1">
                {fhdnPreview.incomplete_examples.map((r, i) => (
                  <li key={i}>{r.charter_name} - {r.item_name}</li>
                ))}
              </ul>
            </details>
          )}
          <button
            onClick={applyFhdnSync}
            disabled={fhdnBusy}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2 text-sm font-semibold disabled:opacity-50"
            data-testid="fhdn-preview-confirm-btn"
          >
            {fhdnBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Apply This Sync
          </button>
        </div>
      )}

      {fhdnResult && (
        <div className="mb-5 rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-4" data-testid="fhdn-result-report">
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold">
              Synced: {fhdnResult.created_activities.length} activity(ies) created, {fhdnResult.updated_activities.length} updated,{' '}
              {fhdnResult.created_sellers.length} charter compan{fhdnResult.created_sellers.length === 1 ? 'y' : 'ies'} added,{' '}
              {fhdnResult.created_categories.length} categor{fhdnResult.created_categories.length === 1 ? 'y' : 'ies'} added.
              New activities are Unpublished - review before making them live.
            </p>
            <button onClick={() => setFhdnResult(null)} className="rounded-lg p-1.5 text-white/40 hover:bg-white/10" data-testid="fhdn-result-close"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {linkCheck && (
        <div className={`mb-5 rounded-xl border p-4 ${linkCheck.broken_count ? 'border-amber-400/30 bg-amber-400/5' : 'border-emerald-400/30 bg-emerald-400/5'}`} data-testid="booking-link-report">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">
                {linkCheck.broken_count
                  ? `${linkCheck.broken_count} booking link(s) are no longer served by the operator`
                  : 'Every booking link opened successfully'}
              </p>
              <p className="text-xs text-white/50">{linkCheck.working} of {linkCheck.checked} links opened with a 200 response.</p>
              {linkCheck.dead_companies?.length > 0 && (
                <p className="mt-1 text-xs text-amber-300">
                  Whole company off FareHarbor: {linkCheck.dead_companies.join(', ')} - fix the shortname or unpublish these activities.
                </p>
              )}
            </div>
            <button onClick={() => setLinkCheck(null)} className="rounded-lg p-1.5 text-white/40 hover:bg-white/10" data-testid="booking-link-report-close"><X className="h-4 w-4" /></button>
          </div>
          {linkCheck.broken?.length > 0 && (
            <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto text-xs">
              {linkCheck.broken.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-black/20 px-2.5 py-1.5">
                  <span className="rounded bg-red-500/20 px-1.5 py-0.5 font-bold text-red-300">{b.http_status || 'ERR'}</span>
                  <span className="font-semibold">{b.title}</span>
                  <span className="text-white/40">{b.seller_name}</span>
                  <button
                    onClick={() => { const a = activities.find((x) => x.id === b.id); if (a) { setModal({ ...emptyActivity, ...a }); setTagInput(''); } }}
                    className="ml-auto text-teal-300 hover:underline"
                    data-testid={`booking-link-fix-${b.alias || b.id}`}
                  >
                    Open
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="activities-filter-bar">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
          <input className={`${inputCls} pl-9`} placeholder="Search by title..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="activities-search-input" />
        </div>
        <select className={`${inputCls} w-auto min-w-[170px]`} value={filterSellerId} onChange={(e) => setFilterSellerId(e.target.value)} data-testid="activities-filter-seller-select">
          <option value="">All Charter Companies</option>
          {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className={`${inputCls} w-auto min-w-[160px]`} value={filterCategoryId} onChange={(e) => setFilterCategoryId(e.target.value)} data-testid="activities-filter-category-select">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="rounded-lg border border-white/10 px-3 py-2 text-xs text-white/50 hover:bg-white/10" data-testid="activities-clear-filters-btn">
            Clear
          </button>
        )}
        <div className="flex items-center gap-1 rounded-lg border border-white/10 p-1">
          <button onClick={() => setViewMode('block')} className={`rounded p-1.5 ${viewMode === 'block' ? 'bg-teal-500/20 text-teal-300' : 'text-white/40 hover:text-white/70'}`} data-testid="activities-view-block-btn" title="Block view">
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('list')} className={`rounded p-1.5 ${viewMode === 'list' ? 'bg-teal-500/20 text-teal-300' : 'text-white/40 hover:text-white/70'}`} data-testid="activities-view-list-btn" title="List view">
            <ListIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-teal-400" /></div>
        : activities.length === 0 ? <div className="rounded-2xl border border-dashed border-white/10 bg-[#0b1f24] py-16 text-center text-white/40" data-testid="activities-empty">No activities yet. Add your first one.</div>
        : filteredActivities.length === 0 ? <div className="rounded-2xl border border-dashed border-white/10 bg-[#0b1f24] py-16 text-center text-white/40" data-testid="activities-filter-empty">No activities match your filters.</div>
        : viewMode === 'list' ? (
          <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0b1f24]" data-testid="activities-list-view">
            {filteredActivities.map((a) => (
              <div key={a.id} className="flex items-center gap-4 border-b border-white/5 p-3 last:border-b-0 hover:bg-white/5" data-testid={`activity-row-${a.id}`}>
                <div className="h-12 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-white/5">
                  {a.images?.[0] ? <img src={a.images[0]} alt={a.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-white/20"><Sparkles className="h-5 w-5" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 truncate font-semibold">
                    {a.featured && <Star className="h-3.5 w-3.5 flex-shrink-0 fill-amber-400 text-amber-400" data-testid={`activity-featured-star-${a.id}`} />}
                    {a.title}
                  </p>
                  <p className="flex items-center gap-1 truncate text-xs text-white/40"><Building2 className="h-3 w-3" /> {a.seller_name}</p>
                </div>
                <div className="hidden flex-wrap gap-1 sm:flex">
                  {a.category_ids?.slice(0, 2).map((cid) => (
                    <span key={cid} className="rounded-full bg-teal-500/15 px-2 py-0.5 text-xs text-teal-300">{catName(cid)}</span>
                  ))}
                </div>
                <span className={`hidden w-24 flex-shrink-0 rounded-full px-2 py-0.5 text-center text-xs md:block ${a.status === 'unpublished' ? 'bg-white/5 text-white/40' : 'bg-emerald-500/15 text-emerald-300'}`} data-testid={`activity-status-${a.id}`}>
                  {a.status === 'unpublished' ? 'Unpublished' : 'Published'}
                </span>
                <span className="hidden w-20 flex-shrink-0 text-xs text-white/40 lg:block">Priority: {a.priority ?? 0}</span>
                <span className="flex w-28 flex-shrink-0 items-center gap-1 text-xs text-white/40">
                  {a.booking_type === 'native_checkout' ? <ShoppingCart className="h-3 w-3" /> : a.booking_provider === 'fareharbor' ? <Anchor className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
                  {a.booking_type === 'native_checkout' ? 'In-App' : a.booking_provider === 'fareharbor' ? 'FareHarbor' : 'External'}
                </span>
                <div className="flex flex-shrink-0 gap-1">
                  <button onClick={() => { setModal({ ...emptyActivity, ...a }); setTagInput(''); }} className="rounded-lg p-1.5 text-white/60 hover:bg-white/10" data-testid={`activity-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => duplicateActivity(a)} className="rounded-lg p-1.5 text-teal-300 hover:bg-teal-500/10" title="Copy this activity" data-testid={`activity-copy-${a.id}`}><Copy className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(a.id, a.title)} className="rounded-lg p-1.5 text-red-300 hover:bg-red-500/10" data-testid={`activity-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="activities-block-view">
            {filteredActivities.map((a) => (
              <div key={a.id} className="overflow-hidden rounded-xl border border-white/10 bg-[#0b1f24]" data-testid={`activity-${a.id}`}>
                <div className="relative h-32 w-full bg-white/5">
                  {a.images?.[0] ? <img src={a.images[0]} alt={a.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-white/20"><Sparkles className="h-8 w-8" /></div>}
                  {a.featured && (
                    <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-xs font-semibold text-black" data-testid={`activity-featured-badge-${a.id}`}>
                      <Star className="h-3 w-3 fill-black" /> Featured
                    </span>
                  )}
                  <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-medium ${a.status === 'unpublished' ? 'bg-black/60 text-white/60' : 'bg-emerald-500/90 text-black'}`} data-testid={`activity-status-${a.id}`}>
                    {a.status === 'unpublished' ? 'Unpublished' : 'Published'}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{a.title}</p>
                      <p className="flex items-center gap-1 text-xs text-white/40"><Building2 className="h-3 w-3" /> {a.seller_name}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { setModal({ ...emptyActivity, ...a }); setTagInput(''); }} className="rounded-lg p-1.5 text-white/60 hover:bg-white/10" data-testid={`activity-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => duplicateActivity(a)} className="rounded-lg p-1.5 text-teal-300 hover:bg-teal-500/10" title="Copy this activity" data-testid={`activity-copy-${a.id}`}><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(a.id, a.title)} className="rounded-lg p-1.5 text-red-300 hover:bg-red-500/10" data-testid={`activity-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  {a.category_ids?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {a.category_ids.map((cid) => (
                        <span key={cid} className="rounded-full bg-teal-500/15 px-2 py-0.5 text-xs text-teal-300">{catName(cid)}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between text-xs text-white/40">
                    <span className="flex items-center gap-1"><Hash className="h-3 w-3" /> Priority {a.priority ?? 0}</span>
                    <span className="flex items-center gap-1">
                      {a.booking_type === 'native_checkout' ? <ShoppingCart className="h-3 w-3" /> : a.booking_provider === 'fareharbor' ? <Anchor className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
                      {a.booking_type === 'native_checkout' ? 'In-App Checkout' : a.booking_provider === 'fareharbor' ? 'FareHarbor' : 'External Booking'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {modal && (
        <Sheet open={!!modal} onOpenChange={(open) => !open && setModal(null)}>
          <SheetContent side="right" className="w-full sm:max-w-xl bg-[#0b1f24] border-white/10 text-white overflow-y-auto" data-testid="activity-modal">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-white"><Sparkles className="h-5 w-5 text-teal-300" /> {modal.id ? 'Edit' : 'New'} Activity</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
            <Accordion type="multiple" defaultValue={['info', 'seo']}>
              <AccordionItem value="info" className={accordionItemCls} data-testid="activity-accordion-info">
                <AccordionTrigger className="text-white hover:no-underline">
                  <span className="flex items-center gap-2 text-sm font-semibold"><Info className="h-4 w-4 text-teal-300" /> Activity Information</span>
                </AccordionTrigger>
                <AccordionContent>
                <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Title</label>
                <input className={inputCls} placeholder="e.g. Sunset Sail" value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} data-testid="activity-title-input" />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Alias</label>
                <input className={inputCls} placeholder="Auto-generated from title if left blank" value={modal.alias} onChange={(e) => setModal({ ...modal, alias: e.target.value })} data-testid="activity-alias-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/60">Status</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setModal({ ...modal, status: 'published' })} className={`flex-1 rounded-lg border px-3 py-2 text-xs ${modal.status === 'published' ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200' : 'border-white/15 text-white/50'}`} data-testid="activity-status-published-btn">
                      Published
                    </button>
                    <button type="button" onClick={() => setModal({ ...modal, status: 'unpublished' })} className={`flex-1 rounded-lg border px-3 py-2 text-xs ${modal.status === 'unpublished' ? 'border-white/40 bg-white/10 text-white' : 'border-white/15 text-white/50'}`} data-testid="activity-status-unpublished-btn">
                      Unpublished
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/60">Priority (display order, lower shows first)</label>
                  <input type="number" className={inputCls} value={modal.priority} onChange={(e) => setModal({ ...modal, priority: parseInt(e.target.value, 10) || 0 })} data-testid="activity-priority-input" />
                </div>
              </div>

              <label className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm">
                <input type="checkbox" checked={modal.featured} onChange={(e) => setModal({ ...modal, featured: e.target.checked })} className="h-4 w-4 accent-amber-400" data-testid="activity-featured-checkbox" />
                <Star className="h-4 w-4 text-amber-400" /> Featured (visual treatment TBD)
              </label>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Charter Company (Seller)</label>
                <div className="flex items-center gap-2">
                  <select className={inputCls} value={modal.seller_id} onChange={(e) => setModal({ ...modal, seller_id: e.target.value })} data-testid="activity-seller-select">
                    <option value="">Select company...</option>
                    {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <button onClick={() => setNewSellerModal({ name: '', fareharbor_shortname: '' })} className="whitespace-nowrap rounded-lg border border-white/10 px-3 py-2 text-xs text-white/60 hover:bg-white/10" data-testid="activity-add-seller-btn">
                    + New Company
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Categories</label>
                <div className="flex flex-wrap gap-1.5" data-testid="activity-category-checkboxes">
                  {categories.length === 0 && <p className="text-xs text-white/30">No categories yet — add one first from Activity Categories.</p>}
                  {orderCategories(categories).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCategory(c.id)}
                      className={`rounded-full border px-3 py-1 text-xs ${modal.category_ids.includes(c.id) ? 'border-teal-400 bg-teal-500/20 text-teal-200' : 'border-white/15 text-white/50 hover:border-white/30'}`}
                      data-testid={`activity-category-toggle-${c.id}`}
                    >
                      {c.parent_name ? `${c.parent_name} \u203a ${c.name}` : c.name}
                    </button>
                  ))}
                </div>
              </div>

              <textarea className={inputCls} rows={3} placeholder="Description (full, shown on the activity detail page)" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} data-testid="activity-description-input" />

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Short Description (teaser shown on public listing cards)</label>
                <textarea className={inputCls} rows={2} placeholder="A short 1-2 sentence teaser for the listing card" value={modal.short_description} onChange={(e) => setModal({ ...modal, short_description: e.target.value })} data-testid="activity-short-description-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Price display (e.g. $150/person)" value={modal.price_display} onChange={(e) => setModal({ ...modal, price_display: e.target.value })} data-testid="activity-price-input" />
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-white/60">From Price (card badge, e.g. 45)</label>
                  <input className={inputCls} placeholder="45" value={modal.price_from} onChange={(e) => setModal({ ...modal, price_from: e.target.value })} data-testid="activity-price-from-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Duration (e.g. 3 hours)" value={modal.duration} onChange={(e) => setModal({ ...modal, duration: e.target.value })} data-testid="activity-duration-input" />
                <input className={inputCls} placeholder="Location (e.g. St. Thomas, USVI)" value={modal.location} onChange={(e) => setModal({ ...modal, location: e.target.value })} data-testid="activity-location-input" />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Tags</label>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Add tag and press Enter" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} data-testid="activity-tag-input" />
                  <button onClick={addTag} className="rounded-lg border border-white/10 px-3 text-xs text-white/60 hover:bg-white/10">Add</button>
                </div>
                {modal.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {modal.tags.map((t) => (
                      <span key={t} className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs">
                        {t} <button onClick={() => removeTag(t)}><X className="h-3 w-3 text-white/50" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Images</label>
                <div className="flex flex-wrap items-center gap-3">
                  {modal.images.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img} alt="" className="h-16 w-24 rounded-lg object-cover" />
                      <button onClick={() => removeImg(idx)} className="absolute -right-2 -top-2 rounded-full bg-black p-0.5 text-red-300"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleImageDrop}
                    onClick={() => fileRef.current?.click()}
                    className="flex h-16 w-24 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-white/20 text-white/40 hover:border-teal-400/50 hover:text-white/60"
                    data-testid="activity-image-upload"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[9px]">Drop or click</span></>}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-white/60">Booking</label>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setModal({ ...modal, booking_type: 'external_link' })} className={`flex-1 rounded-lg border px-3 py-2 text-xs ${modal.booking_type === 'external_link' ? 'border-teal-400 bg-teal-500/20 text-teal-200' : 'border-white/15 text-white/50'}`} data-testid="activity-booking-type-external">
                    <Link2 className="mx-auto mb-1 h-4 w-4" /> External Booking Link
                  </button>
                  <button onClick={() => setModal({ ...modal, booking_type: 'native_checkout' })} className={`flex-1 rounded-lg border px-3 py-2 text-xs ${modal.booking_type === 'native_checkout' ? 'border-teal-400 bg-teal-500/20 text-teal-200' : 'border-white/15 text-white/50'}`} data-testid="activity-booking-type-native">
                    <ShoppingCart className="mx-auto mb-1 h-4 w-4" /> In-App Checkout (soon)
                  </button>
                </div>
                {modal.booking_type === 'external_link' && (
                  <div className="space-y-2 rounded-lg border border-white/10 p-3">
                    <div className="flex gap-2">
                      <button onClick={() => setModal({ ...modal, booking_provider: 'generic' })} className={`flex-1 rounded-lg border px-3 py-1.5 text-xs ${modal.booking_provider === 'generic' ? 'border-teal-400 bg-teal-500/20 text-teal-200' : 'border-white/15 text-white/50'}`} data-testid="activity-booking-provider-generic">
                        Generic URL
                      </button>
                      <button onClick={() => setModal({ ...modal, booking_provider: 'fareharbor' })} className={`flex-1 rounded-lg border px-3 py-1.5 text-xs ${modal.booking_provider === 'fareharbor' ? 'border-teal-400 bg-teal-500/20 text-teal-200' : 'border-white/15 text-white/50'}`} data-testid="activity-booking-provider-fareharbor">
                        <Anchor className="mr-1 inline h-3.5 w-3.5" /> FareHarbor
                      </button>
                    </div>
                    {modal.booking_provider === 'fareharbor' && (
                      <div className="grid grid-cols-2 gap-2">
                        <input className={inputCls} placeholder="Shortname (defaults to company's)" value={modal.fareharbor_shortname} onChange={(e) => setModal({ ...modal, fareharbor_shortname: e.target.value })} data-testid="activity-fareharbor-shortname-input" />
                        <input className={inputCls} placeholder="Item PK (optional)" value={modal.fareharbor_item_pk} onChange={(e) => setModal({ ...modal, fareharbor_item_pk: e.target.value })} data-testid="activity-fareharbor-item-input" />
                      </div>
                    )}
                    {/* The full booking link is never hidden: switching provider must not make an
                        activity's existing link disappear, and a FareHarbor link often carries
                        operator specific parameters (sheet=...) that a rebuilt link would lose. */}
                    <div>
                      {modal.booking_provider === 'fareharbor' && (
                        <label className="mb-1 block text-[11px] font-medium text-white/50">Full booking link (used as-is when set)</label>
                      )}
                      <div className="flex gap-2">
                        <input className={inputCls} placeholder="https://fareharbor.com/embeds/book/company/items/123/..." value={modal.booking_url} onChange={(e) => setModal({ ...modal, booking_url: e.target.value })} data-testid="activity-booking-url-input" />
                        <button
                          type="button"
                          onClick={scrapeActivityPrice}
                          disabled={priceScraping || !modal.booking_url}
                          title="Opens the booking page and reads the price off it - no FareHarbor API access exists, so this is a real (slower) page visit, not an instant lookup"
                          className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-white/15 px-3 text-xs font-semibold text-white/70 hover:text-white disabled:opacity-40"
                          data-testid="activity-get-price-btn"
                        >
                          {priceScraping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DollarSign className="h-3.5 w-3.5" />}
                          {priceScraping ? 'Checking...' : 'Get Price'}
                        </button>
                      </div>
                      {priceResult && (
                        <div className={`mt-2 rounded-lg p-2.5 text-xs ${priceResult.success ? 'bg-emerald-500/10 text-emerald-200' : priceResult.blocked ? 'bg-red-500/10 text-red-200' : 'bg-amber-500/10 text-amber-200'}`} data-testid="activity-price-result">
                          {priceResult.success ? (
                            <div className="flex items-center justify-between gap-2">
                              <span>Found <span className="font-bold">{priceResult.price}</span>{priceResult.snippet ? ` - "${priceResult.snippet}"` : ''}</span>
                              <button onClick={() => applyScrapedPrice(priceResult.price)} className="shrink-0 rounded bg-emerald-500/20 px-2 py-1 font-bold hover:bg-emerald-500/30" data-testid="activity-price-use-btn">Use This Price</button>
                            </div>
                          ) : priceResult.blocked ? (
                            <span className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {priceResult.error} ({priceResult.block_reason})</span>
                          ) : (
                            <span>{priceResult.error}</span>
                          )}
                          {priceResult.candidates?.length > 1 && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {priceResult.candidates.map((c, i) => (
                                <button key={i} onClick={() => applyScrapedPrice(c)} className="rounded-full border border-white/15 px-2 py-0.5 hover:bg-white/10">{c}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {modal.booking_provider === 'fareharbor' && (
                      <div className="rounded-lg border border-white/10 bg-[#061a1f] p-2.5">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-white/50">Booking link the site will open</p>
                        {resolvedBookingUrl(modal) ? (
                          <p className="break-all text-[11px] text-teal-300" data-testid="activity-resolved-booking-url">{resolvedBookingUrl(modal)}</p>
                        ) : (
                          <p className="text-[11px] text-amber-300" data-testid="activity-resolved-booking-url-missing">
                            Add a FareHarbor shortname here or on the charter company - without it there is no booking link.
                          </p>
                        )}
                        <p className={`mt-1.5 text-[11px] ${(sellers.find((x) => x.id === modal.seller_id)?.fareharbor_asn_ref || siteInfo.asnRef) ? 'text-white/40' : 'text-amber-300'}`} data-testid="activity-commission-ref">
                          {(sellers.find((x) => x.id === modal.seller_id)?.fareharbor_asn_ref || siteInfo.asnRef)
                            ? `Commission reference: ${sellers.find((x) => x.id === modal.seller_id)?.fareharbor_asn_ref || siteInfo.asnRef}${sellers.find((x) => x.id === modal.seller_id)?.fareharbor_asn_ref ? ' (this company)' : ' (site wide)'}`
                            : 'No FareHarbor reference set - Developer Settings > FareHarbor. Bookings will NOT be credited.'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
                </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="seo" className={accordionItemCls} data-testid="activity-accordion-seo">
                <AccordionTrigger className="text-white hover:no-underline">
                  <span className="flex items-center gap-2 text-sm font-semibold"><Globe className="h-4 w-4 text-teal-300" /> SEO</span>
                </AccordionTrigger>
                <AccordionContent>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="text-xs font-medium text-white/60">Page Title</label>
                      <span className="text-xs text-white/30">{modal.seo_title.length}/70 characters</span>
                    </div>
                    <input className={inputCls} placeholder={modal.title || 'Page title for search engines'} maxLength={70} value={modal.seo_title} onChange={(e) => setModal({ ...modal, seo_title: e.target.value })} data-testid="activity-seo-title-input" />
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="text-xs font-medium text-white/60">Meta Description</label>
                      <span className="text-xs text-white/30">{modal.seo_description.length}/160 characters</span>
                    </div>
                    <textarea className={inputCls} rows={3} maxLength={160} placeholder="Short description shown under the title in search results" value={modal.seo_description} onChange={(e) => setModal({ ...modal, seo_description: e.target.value })} data-testid="activity-seo-description-input" />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-white/60">URL Handle</label>
                    <div className="flex items-center rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm">
                      <span className="text-white/30">/activities/view/</span>
                      <span className="text-white">{modal.alias || 'auto-generated-from-title'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-white/60">Search Engine Indexing</label>
                    <select className={inputCls} value={modal.seo_robots} onChange={(e) => setModal({ ...modal, seo_robots: e.target.value })} data-testid="activity-seo-robots-select">
                      {ROBOTS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-white/60"><Search className="h-3.5 w-3.5" /> Search Result Preview</label>
                    <div className="rounded-lg bg-white p-4" data-testid="activity-seo-serp-preview">
                      <p className="truncate text-base text-[#1a0dab]">{modal.seo_title || modal.title || 'Activity Title'}</p>
                      <p className="truncate text-sm text-[#006621]" data-testid="activity-seo-serp-url">{siteInfo.siteUrl || 'your-site.com'}/activities/view/{modal.alias || 'auto-generated-from-title'}</p>
                      <p className="mt-0.5 line-clamp-2 text-sm text-[#545454]">{modal.seo_description || modal.description || 'A meta description will appear here once you add one.'}</p>
                    </div>
                  </div>
                </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            </div>

            <button onClick={save} disabled={saving} className="mt-4 w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="activity-save-btn">
              {saving ? 'Saving...' : 'Save Activity'}
            </button>
          </SheetContent>
        </Sheet>
      )}

      {newSellerModal && (
        <Sheet open={!!newSellerModal} onOpenChange={(open) => !open && setNewSellerModal(null)}>
          <SheetContent side="right" className="w-full sm:max-w-sm bg-[#0b1f24] border-white/10 text-white" data-testid="new-seller-modal">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-white"><Building2 className="h-4 w-4 text-teal-300" /> New Charter Company</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3">
              <input className={inputCls} placeholder="Company name" value={newSellerModal.name} onChange={(e) => setNewSellerModal({ ...newSellerModal, name: e.target.value })} data-testid="new-seller-name-input" />
              <input className={inputCls} placeholder="FareHarbor shortname (optional)" value={newSellerModal.fareharbor_shortname} onChange={(e) => setNewSellerModal({ ...newSellerModal, fareharbor_shortname: e.target.value })} data-testid="new-seller-fareharbor-input" />
              <button onClick={createQuickSeller} className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 py-2 text-sm font-semibold" data-testid="new-seller-save-btn">Add Company</button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default Activities;
